<template>
    <div
        class="flex items-center justify-between gap-4 pt-4 pb-6 sm:pt-0 sm:pb-6"
    >
        <button
            class="btn-base btn-white sm:!hidden"
            @click="mobileModalIsOpen = true"
        >
            <Icon icon="filters" class="w-4 h-4" />
            Filters
        </button>
        <div class="sm:flex gap-4 hidden items-center">
            <!-- User Filter (only show if multiple users) -->
            <ButtonPopover
                v-if="availableUsers.length > 1"
                buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                :active-button-class="activeFilterBtnClass"
            >
                <template #button-content>
                    <span class="text-primary text-sm font-medium">User</span>
                    <Transition name="fade">
                        <span
                            v-if="selectedUsers.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedUsers.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default="{ closePopover }">
                    <ul>
                        <li v-for="user in availableUsers" :key="user.id">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-slate-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="selectUser(user, closePopover)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isUserSelected(user)"
                                    @click.stop
                                    @change="selectUser(user)"
                                />
                                <div
                                    class="flex h-5 w-5 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
                                >
                                    <Icon
                                        :icon="
                                            user.is_business
                                                ? 'business'
                                                : 'user'
                                        "
                                        class="w-3 h-3 text-slate-500 dark:text-slate-400"
                                    />
                                </div>
                                <div class="flex flex-col items-start">
                                    <span>{{ user.name }}</span>
                                </div>
                            </button>
                        </li>
                        <li v-if="selectedUsers.length >= 1">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-slate-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="resetUserFilter()"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <!-- Card Filter -->
            <BenefitsCardDropdown />

            <!-- Bank Filter (hidden for freemium users who can only track 1 card) -->
            <ButtonPopover
                v-if="availableBanks.length > 1 && isSubscribed"
                buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                :active-button-class="activeFilterBtnClass"
            >
                <template #button-content>
                    <span class="text-primary text-sm font-medium">Bank</span>
                    <Transition name="fade">
                        <span
                            v-if="selectedBanks.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedBanks.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default="{ closePopover }">
                    <ul>
                        <li v-for="bank in availableBanks" :key="bank.id">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-slate-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="selectBank(bank, closePopover)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isBankSelected(bank)"
                                    @click.stop
                                    @change="selectBank(bank)"
                                />
                                <img
                                    v-if="bank.logo_url"
                                    :src="bank.logo_url"
                                    :alt="bank.name"
                                    class="w-5 h-5 rounded-full shrink-0 object-contain"
                                />
                                <span
                                    v-else
                                    class="w-5 h-5 rounded-full shrink-0 bg-slate-100 dark:bg-slate-700"
                                ></span>
                                <span>{{ bank.name }}</span>
                            </button>
                        </li>
                        <li v-if="selectedBanks.length >= 1">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-slate-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="resetBankFilter()"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <!-- Period Type Filter -->
            <ButtonPopover
                v-if="availablePeriodTypes.length > 1"
                buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                :active-button-class="activeFilterBtnClass"
            >
                <template #button-content>
                    <span class="text-primary text-sm font-medium"
                        >Benefit period</span
                    >
                    <Transition name="fade">
                        <span
                            v-if="selectedPeriodTypes.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedPeriodTypes.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default="{ closePopover }">
                    <ul>
                        <li
                            v-for="periodType in availablePeriodTypes"
                            :key="periodType.id"
                        >
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-slate-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="
                                    selectPeriodType(periodType, closePopover)
                                "
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isPeriodTypeSelected(periodType)"
                                    @click.stop
                                    @change="selectPeriodType(periodType)"
                                />
                                <span>{{ periodType.name }}</span>
                            </button>
                        </li>
                        <li v-if="selectedPeriodTypes.length >= 1">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-slate-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="resetPeriodTypeFilter()"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <!-- Usage Filter -->
            <ButtonPopover
                buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                :active-button-class="activeFilterBtnClass"
            >
                <template #button-content>
                    <span class="text-primary text-sm font-medium">Usage</span>
                    <Transition name="fade">
                        <span
                            v-if="selectedUsageTypes.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedUsageTypes.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default="{ closePopover }">
                    <ul>
                        <li
                            v-for="usageType in usageTypes"
                            :key="usageType.id"
                        >
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-slate-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="
                                    selectUsageType(usageType, closePopover)
                                "
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isUsageTypeSelected(usageType)"
                                    readonly
                                />
                                <span>{{ usageType.name }}</span>
                            </button>
                        </li>
                        <li v-if="selectedUsageTypes.length >= 1">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-slate-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="resetUsageFilter()"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <!-- Category Filter -->
            <ButtonPopover
                v-if="availableCategories.length > 1"
                buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                :active-button-class="activeFilterBtnClass"
            >
                <template #button-content>
                    <span class="text-primary text-sm font-medium"
                        >Category</span
                    >
                    <Transition name="fade">
                        <span
                            v-if="selectedCategories.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedCategories.length }}
                        </span>
                    </Transition>
                    <Icon
                        icon="chevron-down-small"
                        class="w-5 h-5 icons-primary"
                    />
                </template>
                <template #default>
                    <ul>
                        <li
                            v-for="category in availableCategories"
                            :key="category.id"
                        >
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-slate-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="toggleCategory(category)"
                            >
                                <input
                                    type="checkbox"
                                    class="rounded text-10x-orange"
                                    :checked="isCategorySelected(category)"
                                    @click.stop
                                    @change="toggleCategory(category)"
                                />
                                <span>{{ category.name }}</span>
                            </button>
                        </li>
                        <li v-if="selectedCategories.length >= 1">
                            <button
                                type="button"
                                class="w-full p-2 text-sm text-gray-800 dark:text-slate-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                @click="store.resetCategoryFilter()"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <!-- Clear filters button - only shown when filters are active -->
            <button
                v-if="hasActiveFilters"
                class="btn-base btn-transparent !pl-4 !pr-3 !py-1.5"
                @click="clearAllFilters"
            >
                <span class="text-primary text-sm font-medium">Reset all</span>
                <Icon icon="x" class="w-3 h-3 icons-primary" />
            </button>
        </div>
        <!-- Display Options - Desktop: Popover, Mobile: Button to open modal -->
        <BenefitsDisplayPopover v-if="!isMobile" />
        <button
            v-else
            class="btn-icon btn-white !py-1.5 h-10"
            @click="displayModalIsOpen = true"
        >
            <Icon icon="gear" class="w-5 h-5 icons-primary" />
        </button>
    </div>

    <!-- Tracked Cards Selector (for non-subscribed users) -->
    <TrackedCardsSelector
        v-if="!isMobile"
        @open-modal="store.setShowSelectCardsModal(true)"
        @open-upgrade="store.setShowUpgradeModal(true)"
    />

    <BenefitsFiltersMobile
        :is-open="mobileModalIsOpen && isMobile"
        @close="mobileModalIsOpen = false"
        :available-users="availableUsers"
    />
    <BenefitsDisplayModalMobile
        :is-open="displayModalIsOpen && isMobile"
        @close="displayModalIsOpen = false"
    />
</template>
<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useIsMobile } from '@/composables/useIsMobile';
import { useCardBenefitsStore } from '@/store/card-benefits';
import BenefitsDisplayPopover from '@/components/card-benefits/benefits-display-popover.vue';
import BenefitsCardDropdown from '@/components/card-benefits/benefits-card-dropdown.vue';
import BenefitsFiltersMobile from '@/components/card-benefits/benefits-filters-mobile.vue';
import BenefitsDisplayModalMobile from '@/components/card-benefits/benefits-display-modal-mobile.vue';
import TrackedCardsSelector from '@/components/card-benefits/tracked-cards-selector.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import Icon from '@/components/ui/icon/icon.vue';

const store = useCardBenefitsStore();
const {
    selectedUsageTypes,
    selectedUsers,
    selectedBanks,
    selectedPeriodTypes,
    selectedCategories,
    benefitsByCard,
    hasActiveFilters,
    viewMode,
    isSubscribed,
    bannerDismissed,
    walletUsers,
} = storeToRefs(store);

const availableCategories = computed(() => store.availableCategories);

const isCategorySelected = (category) =>
    selectedCategories.value.some((c) => c.id === category.id);
const toggleCategory = (category) => store.toggleCategoryFilter(category);

const { isMobile } = useIsMobile();
const mobileModalIsOpen = ref(false);
const displayModalIsOpen = ref(false);

// Pressed/open state for filter buttons (Figma "Filters. Key Flow"):
// light slate fill while the dropdown is open. Text/chevron stay dark.
const activeFilterBtnClass =
    '!bg-slate-200 hover:!bg-slate-200 dark:!bg-slate-700 dark:hover:!bg-slate-700';

// Available users computed from wallet users and benefits data
const availableUsers = computed(() => {
    // Start with all wallet users to ensure all users are included
    const users = new Map();
    (walletUsers.value || []).forEach((user) => {
        users.set(user.id, {
            id: user.id,
            name: user.name,
            cards: [],
            is_business: false,
        });
    });

    // Add cards from benefits data to their respective user groups
    if (benefitsByCard.value?.groups) {
        benefitsByCard.value.groups.forEach((group) => {
            // If user not in walletUsers, add them (fallback)
            if (group.user && !users.has(group.user.id)) {
                users.set(group.user.id, {
                    id: group.user.id,
                    name: group.user.name,
                    cards: [],
                    is_business: group.user.is_business,
                });
            }

            // Add card to user's cards array
            if (group.user && users.has(group.user.id)) {
                const user = users.get(group.user.id);
                // Update is_business from benefits data if available
                if (group.user.is_business !== undefined) {
                    user.is_business = group.user.is_business;
                }
                const existingCard = user.cards.find(
                    (card) => card.id === group.card.id
                );
                if (!existingCard) {
                    user.cards.push({
                        id: group.card.id,
                        title: group.card.title,
                        image: group.card.image,
                        annual_fee: group.card.annual_fee,
                        connected_account_last_four:
                            group.connected_account_last_four,
                    });
                }
            }
        });
    }

    return Array.from(users.values());
});

// Usage types - static options. IDs stay 'redeemed' / 'not_redeemed'
// (the store filter logic keys off these), only display names change.
const usageTypes = [
    { id: 'redeemed', name: 'Used' },
    { id: 'not_redeemed', name: 'Unused' },
];

// Available banks computed from benefits data
const availableBanks = computed(() => {
    const banks = new Map();

    if (benefitsByCard.value?.groups) {
        benefitsByCard.value.groups.forEach((group) => {
            const bankId = group.card?.issuing_bank_id;
            const bankName = group.card?.issuing_bank_name;

            if (bankId && bankName && !banks.has(bankId)) {
                banks.set(bankId, {
                    id: bankId,
                    name: bankName,
                    logo_url: group.card?.issuing_bank_logo_url || null,
                });
            }
        });
    }

    return Array.from(banks.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
    );
});

// Period type display names mapping
const periodTypeDisplayNames = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    semi_annual: 'Semi-annual',
    annual: 'Annual',
    biennial: 'Every 2 years',
    triennial: 'Every 3 years',
    quadrennial: 'Every 4 years',
    quinquennial: 'Every 5 years',
    decennial: 'Every 10 years',
    statement: 'Per statement',
};

// Cycle length in months — used to sort the Period filter shortest → longest
// (Figma spec).
const periodMonths = {
    statement: 1,
    monthly: 1,
    quarterly: 3,
    semi_annual: 6,
    annual: 12,
    biennial: 24,
    triennial: 36,
    quadrennial: 48,
    quinquennial: 60,
    decennial: 120,
};

// Available period types computed from benefits data
const availablePeriodTypes = computed(() => {
    const periodTypes = new Map();

    if (benefitsByCard.value?.groups) {
        benefitsByCard.value.groups.forEach((group) => {
            group.benefits?.forEach((benefit) => {
                const periodType = benefit.benefit?.period_type?.toLowerCase();

                if (periodType && !periodTypes.has(periodType)) {
                    periodTypes.set(periodType, {
                        id: periodType,
                        name: periodTypeDisplayNames[periodType] || periodType,
                    });
                }
            });
        });
    }

    return Array.from(periodTypes.values()).sort(
        (a, b) =>
            (periodMonths[a.id] ?? Infinity) -
            (periodMonths[b.id] ?? Infinity)
    );
});

const clearAllFilters = () => {
    store.clearFilters();
};

// Selection methods for usage types
const isUsageTypeSelected = (usageType) => {
    return selectedUsageTypes.value.some(
        (selected) => selected.id === usageType.id
    );
};

const toggleUsageTypeSelection = (usageType) => {
    store.toggleUsageFilter(usageType);
};

const selectUsageType = (usageType, closePopover) => {
    toggleUsageTypeSelection(usageType);
};

const resetUsageFilter = () => {
    store.resetUsageFilter();
};

// Selection methods for users
const isUserSelected = (user) => {
    return selectedUsers.value.some((selected) => selected.id === user.id);
};

const selectUser = (user, closePopover) => {
    store.toggleUserFilter(user);
    // Don't close popover to allow multiple selections
};

const resetUserFilter = () => {
    store.resetUserFilter();
};

// Selection methods for banks
const isBankSelected = (bank) => {
    return selectedBanks.value.some((selected) => selected.id === bank.id);
};

const selectBank = (bank, closePopover) => {
    store.toggleBankFilter(bank);
};

const resetBankFilter = () => {
    store.resetBankFilter();
};

// Selection methods for period types
const isPeriodTypeSelected = (periodType) => {
    return selectedPeriodTypes.value.some(
        (selected) => selected.id === periodType.id
    );
};

const selectPeriodType = (periodType, closePopover) => {
    store.togglePeriodTypeFilter(periodType);
};

const resetPeriodTypeFilter = () => {
    store.resetPeriodTypeFilter();
};

// Watch for view mode changes and reset filters
watch(viewMode, () => {
    if (hasActiveFilters.value) {
        store.clearFilters();
    }
});
</script>
